import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  isDevMode,
  OnInit,
  PLATFORM_ID,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { NavigationEnd, NavigationError, Router, RouterOutlet } from '@angular/router';
import { SwUpdate, UnrecoverableStateEvent, VersionReadyEvent } from '@angular/service-worker';
import { TranslateService } from '@ngx-translate/core';
import {
  AppReadyService,
  AVAILABLE_LANGUAGES,
  CommonMetaService,
  CookiesConsentComponent,
  DEFAULT_LANGUAGE,
  GtagService,
  LightboxComponent,
  LOADING_STATE_MESSAGES,
  LoadingComponent,
  LoadingState,
  PullToRefreshDirective,
  RouteLoadingService,
  SentryLoggerService,
  SharedModule,
} from '@shared';
import { filter, take } from 'rxjs';

@Component({
  selector: 'app-root',
  imports: [ RouterOutlet, SharedModule, CookiesConsentComponent, LightboxComponent, LoadingComponent, PullToRefreshDirective ],
  templateUrl: './app.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'contents',
  },
})
export class AppComponent implements OnInit {
  private document: Document = inject(DOCUMENT);
  private commonMeta: CommonMetaService = inject(CommonMetaService);
  private gtagService: GtagService = inject(GtagService);
  private swUpdate: SwUpdate = inject(SwUpdate);
  private translate: TranslateService = inject(TranslateService);
  private sentryLogger: SentryLoggerService = inject(SentryLoggerService);
  private platformId: object = inject(PLATFORM_ID);
  private routeLoadingService: RouteLoadingService = inject(RouteLoadingService);
  private router: Router = inject(Router);
  private appReady: AppReadyService = inject(AppReadyService);
  private destroyRef: DestroyRef = inject(DestroyRef);
  private dialog: MatDialog = inject(MatDialog);

  private readonly isBrowser = isPlatformBrowser(this.platformId);
  private readonly UPDATE_RELOAD_KEY = 'klubovna_update_reload';
  private loadingElement: HTMLElement | null = null;
  private progressBarElement: HTMLElement | null = null;
  private loadingTextElement: HTMLElement | null = null;
  private isUpdating = false;

  protected readonly routeLazyLoadingSig = this.routeLoadingService.routeLoadingOverlayVisibleSig;
  protected readonly initialLoaderActiveSig = signal(true);

  ngOnInit() {
    this.setMeta();
    this.setLang();

    if (!this.isBrowser) {
      this.initialLoaderActiveSig.set(false);
      this.appReady.setReady();
      return;
    }

    this.loadingElement = this.document.querySelector('.loading-initial');
    this.progressBarElement = this.document.querySelector('.progress-initial');
    this.loadingTextElement = this.document.querySelector('.loading-text') as HTMLElement;

    this.clearHtmlInterval();
    this.setProgress(this.getCurrentProgress());

    this.gtagService.initializeTracking();
    this.setupSwEventHandlers();
    this.setupNavigationErrorHandler();

    this.runStartupSequence();
  }

  private async runStartupSequence(): Promise<void> {
    this.setLoadingState(LoadingState.CHECKING_UPDATES);
    this.animateProgressTo(50);

    const hasUpdate = await this.checkForUpdate();

    if (hasUpdate) {
      this.setLoadingState(LoadingState.DOWNLOADING_UPDATE);
      this.animateProgressTo(75);
      await this.waitForUpdateAndReload();
    }

    this.setLoadingState(LoadingState.LOADING);
    this.animateProgressTo(80);

    await this.waitForRouter();

    this.animateProgressTo(100, () => {
      this.hideLoading();
    });
  }

  private checkForUpdate(): Promise<boolean> {
    if (isDevMode() || !this.swUpdate.isEnabled || this.wasRecentlyUpdated()) {
      return Promise.resolve(false);
    }

    return new Promise((resolve) => {
      const timeoutId = window.setTimeout(() => resolve(false), 3000);

      this.swUpdate.checkForUpdate()
        .then((hasUpdate) => {
          clearTimeout(timeoutId);
          resolve(hasUpdate);
        })
        .catch((err) => {
          clearTimeout(timeoutId);
          this.sentryLogger.captureException(err, {
            component: 'AppComponent',
            action: 'checkForUpdate',
          });
          resolve(false);
        });
    });
  }

  private waitForUpdateAndReload(): Promise<void> {
    this.isUpdating = true;

    return new Promise((resolve) => {
      const timeoutId = window.setTimeout(() => {
        this.swUpdate.activateUpdate()
          .then(() => {
            this.markUpdateReload();
            this.document.location.reload();
          })
          .catch(() => {
            this.isUpdating = false;
            resolve();
          });
      }, 5000);

      this.swUpdate.versionUpdates
        .pipe(
          filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY'),
          take(1),
        )
        .subscribe(() => {
          clearTimeout(timeoutId);
          this.setLoadingState(LoadingState.INSTALLING_UPDATE);
          this.animateProgressTo(95);

          this.swUpdate.activateUpdate()
            .then(() => {
              this.markUpdateReload();
              this.document.location.reload();
            })
            .catch(() => {
              this.isUpdating = false;
              resolve();
            });
        });
    });
  }

  private waitForRouter(): Promise<void> {
    return new Promise((resolve) => {
      if (this.router.navigated) {
        resolve();
        return;
      }

      this.router.events
        .pipe(
          filter((e) => e instanceof NavigationEnd || e instanceof NavigationError),
          take(1),
        )
        .subscribe(() => resolve());
    });
  }

  private hideLoading(): void {
    this.document.body.classList.remove('no-scroll');
    this.initialLoaderActiveSig.set(false);
    this.appReady.setReady();

    if (!this.loadingElement) {
      return;
    }

    this.loadingElement.classList.add('fade-out');
    this.loadingElement.addEventListener('animationend', () => {
      this.loadingElement?.remove();
    }, { once: true });
  }

  private setupSwEventHandlers(): void {
    if (isDevMode() || !this.swUpdate.isEnabled) {
      return;
    }

    this.swUpdate.versionUpdates
      .pipe(
        filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY'),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        if (this.isUpdating) {
          return;
        }

        this.swUpdate.activateUpdate()
          .then(() => this.showVersionUpdateDialog())
          .catch(() => this.showVersionUpdateDialog());
      });

    this.swUpdate.unrecoverable
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event: UnrecoverableStateEvent) => {
        this.sentryLogger.captureException(new Error('SW Unrecoverable State'), {
          component: 'AppComponent',
          action: 'unrecoverableState',
          extra: { reason: event.reason },
        });
        this.clearCachesAndReload();
      });

    this.document.addEventListener('visibilitychange', () => {
      if (!this.document.hidden && !this.isUpdating && !this.initialLoaderActiveSig()) {
        this.swUpdate.checkForUpdate()
          .then(hasUpdate => {
            if (hasUpdate) {
              this.showVersionUpdateDialog();
            }
          })
          .catch(() => {});
      }
    });
  }

  private showVersionUpdateDialog(): void {
    if (this.dialog.openDialogs.length > 0 || this.wasRecentlyUpdated()) {
      return;
    }

    import('@shared').then(m => {
      this.dialog.open(m.VersionUpdateDialogComponent, {
        disableClose: true,
        panelClass: 'custom-dialog-panel',
        backdropClass: 'custom-dialog-backdrop',
      });
    });
  }

  private setupNavigationErrorHandler(): void {
    this.router.events
      .pipe(
        filter((e): e is NavigationError => e instanceof NavigationError),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((event) => {
        const error = event.error;
        const message = error?.message || error?.toString() || '';

        if (this.isChunkLoadError(message)) {
          this.sentryLogger.captureException(error, {
            component: 'AppComponent',
            action: 'navigationError',
            extra: { url: event.url },
          });

          sessionStorage.setItem('chunkErrorRetryUrl', event.url);

          this.hideLoading();
          this.routeLoadingService.forceHide();
          this.showChunkErrorScreen();
        }
      });
  }

  private isChunkLoadError(message: string): boolean {
    return (
      message.includes('ChunkLoadError') ||
      message.includes('Loading chunk') ||
      message.includes('Failed to fetch dynamically imported module') ||
      message.includes('Importing a module script failed')
    );
  }

  private showChunkErrorScreen(): void {
    const existing = this.document.querySelector('.chunk-error-overlay');
    if (existing) {
      return;
    }

    const container = this.document.createElement('div');
    container.className = 'chunk-error-overlay';
    container.innerHTML = `
      <div style="position: fixed; inset: 0; background: #0a0a0a; z-index: 9999; display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 2rem; text-align: center;">
        <span class="material-symbols-outlined" style="font-size: 80px; color: rgba(255,255,255,0.2); margin-bottom: 1rem;">cloud_off</span>
        <h2 style="color: white; font-size: 1.5rem; font-weight: 600; margin-bottom: 0.5rem;">Chyba na\u010D\u00EDtania</h2>
        <p style="color: rgba(255,255,255,0.6); margin-bottom: 1.5rem; max-width: 400px;">Niektor\u00E9 \u010Dasti aplik\u00E1cie sa nepodarilo na\u010D\u00EDta\u0165.</p>
        <button style="padding: 0.75rem 1.5rem; background: #d4f53c; color: #0a0a0a; border: none; border-radius: 0.5rem; font-weight: 500; cursor: pointer;">
          Obnovi\u0165 str\u00E1nku
        </button>
      </div>
    `;

    const button = container.querySelector('button');
    if (button) {
      button.addEventListener('click', () => this.clearCachesAndRetry());
    }

    this.document.body.appendChild(container);
  }

  private async clearCachesAndRetry(): Promise<void> {
    const retryUrl = sessionStorage.getItem('chunkErrorRetryUrl');
    sessionStorage.removeItem('chunkErrorRetryUrl');

    try {
      const cacheNames = await caches.keys();
      await Promise.all(cacheNames.map(name => caches.delete(name)));

      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map(reg => reg.unregister()));
    } catch (e) {
      console.error('Failed to clear caches:', e);
    }

    this.document.location.href = retryUrl || '/';
  }

  private async clearCachesAndReload(): Promise<void> {
    try {
      const cacheNames = await caches.keys();
      await Promise.all(cacheNames.map(name => caches.delete(name)));

      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map(reg => reg.unregister()));
    } catch (e) {
      console.error('Failed to clear caches:', e);
    }

    this.markUpdateReload();
    this.document.location.reload();
  }

  private clearHtmlInterval(): void {
    const interval = (window as any).__initialProgressInterval;
    if (interval) {
      clearInterval(interval);
      delete (window as any).__initialProgressInterval;
    }
  }

  private getCurrentProgress(): number {
    if (!this.progressBarElement) return 0;
    return parseFloat(this.progressBarElement.style.width) || 0;
  }

  private setProgress(value: number): void {
    if (this.progressBarElement) {
      this.progressBarElement.style.width = `${ value }%`;
    }
  }

  private setLoadingText(text: string): void {
    if (this.loadingTextElement) {
      this.loadingTextElement.textContent = text;
    }
  }

  private setLoadingState(state: LoadingState): void {
    const lang = this.translate.getCurrentLang() === 'en' ? 'en' : 'sk';
    const message = LOADING_STATE_MESSAGES[state][lang];
    this.setLoadingText(message);
  }

  private animateProgressTo(target: number, onComplete?: () => void): void {
    const start = this.getCurrentProgress();
    if (start >= target) {
      onComplete?.();
      return;
    }

    const duration = 300;
    const startTime = performance.now();

    const animate = (now: number) => {
      const elapsed = now - startTime;
      const t = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - t, 3);
      this.setProgress(start + (target - start) * eased);

      if (t < 1) {
        requestAnimationFrame(animate);
      } else {
        onComplete?.();
      }
    };

    requestAnimationFrame(animate);
  }

  private setLang(): void {
    this.translate.addLangs(AVAILABLE_LANGUAGES);
    this.translate.use(DEFAULT_LANGUAGE);
  }

  private setMeta(): void {
    this.commonMeta.setRobotsTag();
  }

  private markUpdateReload(): void {
    sessionStorage.setItem(this.UPDATE_RELOAD_KEY, Date.now().toString());
  }

  private wasRecentlyUpdated(): boolean {
    const timestamp = sessionStorage.getItem(this.UPDATE_RELOAD_KEY);
    if (!timestamp) return false;

    sessionStorage.removeItem(this.UPDATE_RELOAD_KEY);
    const GRACE_PERIOD_MS = 30_000;
    return (Date.now() - parseInt(timestamp, 10)) < GRACE_PERIOD_MS;
  }
}
